import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `<LocaleProvider>` + `useLocale()` — i18n surface for the PHG
 * web client.
 *
 * The provider holds the patient-requested locale + the current
 * effective locale (after fallback resolution) + the bundle
 * registry; the hook exposes `t()` for views to consume +
 * `setLocale()` for the preference action.
 *
 * **Doctrinal posture (PFO §2.3 + §7.7):**
 *   - Patient's *requested* locale is preserved through state even
 *     when not in the registry — UX uses this distinction to
 *     surface the accommodation pathway honestly. PHG cannot
 *     promise instant translation coverage; the language-access
 *     policy SLA is the load-bearing operational discipline.
 *   - Fallback resolution is single-step: if requested locale is
 *     not in `availableLocales`, the effective locale is the
 *     `DEFAULT_LOCALE`. v1.5+ may add related-language fallback
 *     (e.g. `es-MX` → `es-ES` if available); v0 keeps the simple
 *     posture so accommodation routing is clearly the patient's
 *     primary surface for non-supported locales.
 *   - Missing message keys fall back to the key itself rather
 *     than empty string (defense against accidental rendering of
 *     blank UI; the key is grep-able + observability-trackable).
 *     v1 observability hooks should SLO-track missing-key rates.
 */
import * as React from 'react';
import { BUNDLE_REGISTRY, DEFAULT_LOCALE } from './messages/index.js';
const LocaleContext = React.createContext(null);
export function LocaleProvider(props) {
    const registry = props.registry ?? BUNDLE_REGISTRY;
    const [requestedLocale, setRequestedLocale] = React.useState(props.initialLocale ?? DEFAULT_LOCALE);
    const availableLocales = React.useMemo(() => Object.keys(registry).sort(), [registry]);
    const requestedLocaleSupported = registry[requestedLocale] !== undefined;
    const effectiveLocale = requestedLocaleSupported
        ? requestedLocale
        : DEFAULT_LOCALE;
    const messages = registry[effectiveLocale]?.messages ?? {};
    const t = React.useCallback((key, params) => {
        const raw = messages[key];
        if (raw === undefined) {
            // Missing key: fall back to the key itself (grep-able for
            // bundle-coverage audits) and warn in dev.
            if (typeof console !== 'undefined' && console.warn !== undefined) {
                console.warn(`[i18n] missing message key: ${key} (locale: ${effectiveLocale})`);
            }
            return key;
        }
        if (params === undefined || Object.keys(params).length === 0) {
            return raw;
        }
        return substituteParams(raw, params);
    }, [messages, effectiveLocale]);
    const setLocale = React.useCallback((locale) => {
        setRequestedLocale(locale);
    }, []);
    const value = {
        locale: effectiveLocale,
        requestedLocale,
        requestedLocaleSupported,
        availableLocales,
        setLocale,
        t,
    };
    return _jsx(LocaleContext.Provider, { value: value, children: props.children });
}
/**
 * `useLocale()` — consume the locale context. Throws when called
 * outside a `<LocaleProvider>` ancestor; this surfaces
 * provider-misconfiguration as a load-time error rather than
 * silently rendering with a default locale (which would hide
 * configuration bugs in production).
 */
export function useLocale() {
    const value = React.useContext(LocaleContext);
    if (value === null) {
        throw new Error('useLocale() called outside <LocaleProvider>. Wrap the app shell in <LocaleProvider> at index.tsx.');
    }
    return value;
}
/**
 * Double-curly param substitution: `"{{name}}"` → `params.name`.
 * Stable; no positional / pluralization / gender / ordinal
 * features at v0 (per the framework choice posture: scaffold for
 * it, don't build it). Patient-Facing Terms v1.3 may surface
 * pluralization needs that v1.5+ implements.
 */
function substituteParams(template, params) {
    return template.replace(/\{\{(\w+)\}\}/g, (match, name) => {
        if (Object.prototype.hasOwnProperty.call(params, name)) {
            return String(params[name]);
        }
        return match;
    });
}
//# sourceMappingURL=locale-context.js.map